// communicatie (data-integrity guard): decides whether a template body is a fixed-layout "raw-print /
// db-doc" document that must NOT be edited through the TipTap WYSIWYG.
//
// WHY: Jmix has a separate raw-print mode (comm-editor.js `_refreshRawDoc`, CommunicationDetailView
// `isRawPrintTemplate`) for fixed-layout legal documents (e.g. afstandsverklaring). Those bodies carry
// their own print CSS + exact table layout; running them through TipTap reflows/normalizes the markup and
// corrupts the document. The React editor previously loaded EVERY body into TipTap, so opening + saving
// such a template would silently corrupt it. This detector routes those bodies to a raw-HTML editor
// instead (edits the markup verbatim, never reflowed).
//
// DISCRIMINATOR — a faithful port of the Jmix one, plus a safe-side fallback:
//  1. Jmix `CommunicationDetailView.isRawPrintTemplate(t)` returns `body.contains("db-doc")` — the
//     fixed-layout documents are wrapped in `<div class="db-doc">`. We mirror that exactly (primary).
//  2. SAFE-SIDE fallback: any body that is a full HTML document or carries print/page CSS
//     (`<html`, `<head`, `<body`, or `@page`) is also treated as raw-print. Normal templates are HTML
//     FRAGMENTS (`<p>…`, `<h2>…`), never full documents, so this never misroutes a normal template — and
//     when in doubt we default to the lossless raw editor (the task's "never let a fixed-layout doc
//     round-trip through TipTap" rule).

const DB_DOC_MARKER = "db-doc";

// Full-document / fixed-layout print markers (case-insensitive). A normal template body is a fragment and
// contains none of these.
const FULL_DOC_PATTERNS = [/<html[\s>]/i, /<head[\s>]/i, /<body[\s>]/i, /@page\b/i];

/**
 * True when `bodyHtml` is a fixed-layout raw-print ("db-doc") document that must be edited as raw HTML
 * (never through TipTap). Mirrors Jmix `isRawPrintTemplate` (the `db-doc` wrapper) and adds a safe-side
 * full-document / `@page` heuristic so an unmarked fixed-layout document is still protected.
 */
export function isRawPrintBody(bodyHtml: string | null | undefined): boolean {
  if (!bodyHtml) return false;
  if (bodyHtml.includes(DB_DOC_MARKER)) return true;
  return FULL_DOC_PATTERNS.some((re) => re.test(bodyHtml));
}
